import { useEffect, useState } from 'react';

/**
 * Selectors for third-party UI that is fixed to the bottom of the viewport and
 * which the sticky purchase bar must sit ABOVE (never overlap). Extend this
 * list when a new bottom-fixed widget is added.
 *
 * - `#onetrust-banner-sdk` — OneTrust cookie consent banner.
 * - `.ot-floating-button` — OneTrust post-consent "cookie settings" floating
 *   button (a consent control that must stay visible/clickable). No-op if the
 *   floating icon isn't enabled in the OneTrust config.
 * - `.search-container` — Salesforce/Agentforce bottom search-chat bar
 *   (currently disabled by env flag; registered so the bar auto-adjusts if it
 *   is ever enabled — no code change required).
 */
const BOTTOM_FIXED_SELECTORS = [
  '#onetrust-banner-sdk',
  '.ot-floating-button',
  '.search-container',
];

export const STICKY_BOTTOM_OFFSET_VAR = '--sticky-pdp-bottom-offset';

/** A rendered element anchored to (touching) the bottom of the viewport. */
const isVisibleBottomFixed = (el: Element, viewportHeight: number): boolean => {
  const rect = el.getBoundingClientRect();
  return (
    rect.height > 0 &&
    rect.width > 0 &&
    rect.top >= 0 && // ignore full-screen overlays (top above viewport)
    rect.bottom >= viewportHeight - 4 // touches the viewport bottom
  );
};

/**
 * Height occupied by the tallest bottom-anchored widget currently on screen.
 * `max` (via the smallest `rect.top`) — not sum — because these widgets are all
 * bottom-anchored and overlap each other; the occupied height is the tallest.
 * Clamped to the viewport so a taller-than-screen widget can't push the bar
 * into the header or open an oversized gap.
 */
const measureBottomFixedOffset = (): number => {
  const viewportHeight = globalThis.window.innerHeight;
  let topMost = viewportHeight;

  for (const selector of BOTTOM_FIXED_SELECTORS) {
    for (const el of Array.from(document.querySelectorAll(selector))) {
      if (isVisibleBottomFixed(el, viewportHeight)) {
        topMost = Math.min(topMost, el.getBoundingClientRect().top);
      }
    }
  }

  return Math.min(
    viewportHeight,
    Math.max(0, Math.round(viewportHeight - topMost)),
  );
};

/**
 * Measures the tallest bottom-anchored third-party widget currently on screen
 * and exposes its height both as a returned number (for the page spacer) and as
 * a CSS custom property (`--sticky-pdp-bottom-offset`) on <html> (for the bar's
 * `bottom`). This lets the sticky purchase bar stack ABOVE cookie banners / chat
 * bars instead of overlapping them, in any combination, updating live as those
 * widgets mount, unmount or resize.
 *
 * @param enabled Only measure while the bar is visible (skips work otherwise).
 */
export const useBottomFixedOffset = (enabled: boolean): number => {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (!enabled || typeof globalThis.window === 'undefined') {
      setOffset(0);
      return;
    }

    let frame = 0;

    const measure = () => {
      const next = measureBottomFixedOffset();
      setOffset((prev) => (prev === next ? prev : next));
    };

    // Debounce all triggers to one measurement per frame.
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };

    schedule();

    // Third-party widgets mount/unmount late (script-injected).
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    globalThis.window.addEventListener('resize', schedule);
    // Re-measure once a widget finishes any slide-in transition (transform
    // animations aren't caught by the MutationObserver).
    document.addEventListener('transitionend', schedule, true);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      globalThis.window.removeEventListener('resize', schedule);
      document.removeEventListener('transitionend', schedule, true);
    };
  }, [enabled]);

  // Reflect the measured offset to the CSS variable the bar reads.
  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const root = document.documentElement;
    root.style.setProperty(STICKY_BOTTOM_OFFSET_VAR, `${offset}px`);
    return () => {
      root.style.removeProperty(STICKY_BOTTOM_OFFSET_VAR);
    };
  }, [offset]);

  return offset;
};
